import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { toast } from 'sonner';

const TABS = [
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'past', label: 'Past' },
  { id: 'cancelled', label: 'Cancelled' },
];

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);

const TicketCutout = ({ children }) => (
  <div className="relative">
    <div className="absolute w-5 h-5 bg-[#F9FAFB] rounded-full -left-2.5 bottom-24 z-10" />
    <div className="absolute w-5 h-5 bg-[#F9FAFB] rounded-full -right-2.5 bottom-24 z-10" />
    {children}
  </div>
);

const TicketCard = ({ booking }) => {
  const [isReminderSet, setIsReminderSet] = useState(false);
  const ticketId = `#BY-${booking.id.slice(0, 6).toUpperCase()}`;
  const seatSection = booking.items[0]?.tier_name ? `${booking.items[0].tier_name}` : 'General Entry';

  return (
    <TicketCutout>
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100" data-testid="ticket-card">
        <div className="p-5 bg-[#2B3A67] text-white flex justify-between items-start">
          <div>
            <div className="bg-[#3AAFA9]/20 text-[#3AAFA9] text-[10px] font-black px-2 py-0.5 rounded border border-[#3AAFA9]/30 inline-block mb-2 uppercase tracking-widest">
              VIP PASS
            </div>
            <h2 className="text-xl font-bold leading-tight" data-testid="ticket-event-title">
              {booking.event_title}
            </h2>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-bold text-gray-300 uppercase tracking-widest">Ticket ID</p>
            <p className="text-xs font-mono font-bold" data-testid="ticket-id">{ticketId}</p>
          </div>
        </div>

        <div className="p-5 grid grid-cols-2 gap-4 border-b border-dashed border-gray-200">
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Date & Time</p>
            <p className="text-sm font-bold" data-testid="ticket-date">{new Date(booking.event_date).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Location</p>
            <p className="text-sm font-bold truncate" data-testid="ticket-location">{booking.venue_name}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Seat/Section</p>
            <p className="text-sm font-bold" data-testid="ticket-seat">{seatSection}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Order Total</p>
            <p className="text-sm font-bold" data-testid="ticket-total">{formatCurrency(booking.total_amount)}</p>
          </div>
        </div>

        <div className="p-6 flex flex-col items-center justify-center bg-gray-50/50">
          <div className="bg-white p-3 rounded-2xl shadow-sm mb-4 border border-gray-100">
            <div className="w-32 h-32 bg-[#2B3A67] flex items-center justify-center rounded-lg">
              <div className="grid grid-cols-3 gap-1 p-2 bg-white rounded">
                {Array.from({ length: 9 }).map((_, idx) => (
                  <div
                    key={idx}
                    className={`w-6 h-6 ${idx === 4 ? 'border-2 border-[#2B3A67]' : 'bg-[#2B3A67]'}`}
                  />
                ))}
              </div>
            </div>
          </div>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em]" data-testid="ticket-qr-label">
            Scan at Entrance
          </p>
        </div>

        <div className="px-5 pb-6 flex gap-3">
          <Link
            to={`/bookings/${booking.id}`}
            className="flex-1 flex items-center justify-center gap-2 bg-white border-2 border-[#2B3A67] text-[#2B3A67] font-bold py-3 rounded-xl text-sm"
            data-testid="ticket-view-button"
          >
            <i className="ph-bold ph-share-network"></i> View
          </Link>
          <button
            onClick={() => setIsReminderSet(!isReminderSet)}
            className={`flex-1 flex items-center justify-center gap-2 font-bold py-3 rounded-xl text-sm ${
              isReminderSet ? 'bg-[#2B3A67] text-white' : 'bg-[#3AAFA9] text-white'
            }`}
            data-testid="ticket-reminder-button"
          >
            <i className="ph-fill ph-bell-ringing"></i>
            {isReminderSet ? 'Reminder Set' : 'Remind Me'}
          </button>
        </div>
      </div>
    </TicketCutout>
  );
};

const CompactTicketCard = ({ booking }) => {
  const eventDate = new Date(booking.event_date);
  const month = eventDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  const day = eventDate.getDate();

  return (
    <div className="bg-white rounded-2xl p-4 flex gap-4 shadow-md border border-gray-50" data-testid="compact-ticket-card">
      <div className="w-20 h-20 bg-[#E54562] rounded-xl flex flex-col items-center justify-center text-white shrink-0">
        <span className="text-xs font-bold uppercase">{month}</span>
        <span className="text-2xl font-black">{day}</span>
      </div>
      <div className="flex-1">
        <h4 className="font-bold text-[#2B3A67] leading-tight mb-1" data-testid="compact-ticket-title">
          {booking.event_title}
        </h4>
        <p className="text-xs text-gray-500 flex items-center gap-1 mb-3" data-testid="compact-ticket-location">
          <i className="ph-fill ph-map-pin text-[#E54562]"></i> {booking.venue_name}
        </p>
        <div className="flex gap-2">
          <Link
            to={`/bookings/${booking.id}`}
            className="text-[10px] font-black uppercase tracking-wider text-[#3AAFA9] border border-[#3AAFA9] px-2 py-1 rounded"
            data-testid="compact-ticket-view"
          >
            View Ticket
          </Link>
          <button
            className="text-[10px] font-black uppercase tracking-wider text-gray-400 border border-gray-200 px-2 py-1 rounded"
            data-testid="compact-ticket-resell"
          >
            Resell
          </button>
        </div>
      </div>
    </div>
  );
};

const SafetyReminder = () => (
  <div className="bg-[#F4D341]/10 border-2 border-[#F4D341] rounded-2xl p-5 flex items-start gap-4" data-testid="ticket-safety-reminder">
    <div className="w-10 h-10 bg-[#F4D341] rounded-full flex items-center justify-center shrink-0 shadow-sm">
      <i className="ph-fill ph-warning-circle text-[#2B3A67] text-xl"></i>
    </div>
    <div>
      <h4 className="font-bold text-[#2B3A67] text-sm">Safety Reminder</h4>
      <p className="text-xs text-[#2B3A67]/70 leading-relaxed mt-1">
        Only buy tickets from verified sellers. Tickets transferred outside of Backyard are not guaranteed.
      </p>
    </div>
  </div>
);

const EmptyTickets = ({ onBrowse }) => {
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [promoCode, setPromoCode] = useState('');

  return (
    <div className="flex flex-col items-center justify-center px-8 pt-16" data-testid="tickets-empty-state">
      <div className="relative w-64 h-64 mb-10 flex items-center justify-center">
        <div className="absolute inset-0 rounded-full scale-110 bg-[#3AAFA9]/10"></div>
        <div className="absolute top-4 right-4 w-12 h-12 rounded-full blur-xl bg-[#F4D341]/20"></div>
        <div className="absolute bottom-10 left-4 w-16 h-16 rounded-full blur-lg bg-[#E54562]/10"></div>

        <div className="relative transform -rotate-12">
          <div className="w-40 h-56 bg-white border-2 border-dashed border-gray-200 rounded-2xl absolute top-4 left-4"></div>

          <div className="w-40 h-56 bg-white border-2 rounded-2xl shadow-2xl relative overflow-hidden flex flex-col p-4 border-[#2B3A67]">
            <div className="w-full h-1/2 border-b-2 border-dashed border-gray-200 flex items-center justify-center mb-4">
              <i className="ph-duotone ph-ticket text-6xl text-gray-200"></i>
            </div>
            <div className="flex flex-col gap-2">
              <div className="h-3 w-3/4 bg-gray-100 rounded-full"></div>
              <div className="h-2 w-1/2 bg-gray-100 rounded-full"></div>
              <div className="h-2 w-2/3 bg-gray-100 rounded-full"></div>
            </div>
            <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-gray-50 rounded-full border-r-2 border-[#2B3A67]"></div>
            <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-gray-50 rounded-full border-l-2 border-[#2B3A67]"></div>
          </div>
        </div>

        <div className="absolute bottom-6 right-6 w-16 h-16 rounded-2xl shadow-lg border-4 border-white flex items-center justify-center transform rotate-12 bg-[#F4D341]">
          <i className="ph-bold ph-magnifying-glass text-3xl text-[#2B3A67]"></i>
        </div>
      </div>

      <div className="text-center max-w-xs">
        <h2 className="text-2xl font-extrabold mb-3 text-[#2B3A67]">No Tickets Yet?</h2>
        <p className="text-gray-500 font-medium leading-relaxed">
          The party hasn't started! Browse the latest events and grab your spot on the guest list.
        </p>
      </div>

      <button
        onClick={onBrowse}
        className="mt-10 w-full py-4 rounded-2xl text-white font-bold shadow-[4px_4px_0px_0px_rgba(229,69,98,1)] hover:translate-y-[-2px] hover:shadow-[6px_6px_0px_0px_rgba(229,69,98,1)] transition-all active:translate-y-[2px] active:shadow-none flex items-center justify-center gap-2 bg-[#2B3A67]"
        data-testid="tickets-browse-button"
      >
        <i className="ph-bold ph-sparkle text-[#F4D341]"></i>
        Browse Trending Events
      </button>

      <button
        onClick={() => setShowPromoModal(true)}
        className="mt-4 font-bold text-sm tracking-wide uppercase text-[#E54562]"
        data-testid="tickets-promo-button"
      >
        Enter Promo Code
      </button>

      {showPromoModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] px-6"
          onClick={() => setShowPromoModal(false)}
          data-testid="promo-modal"
        >
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-[#2B3A67]">Enter Promo Code</h3>
              <button onClick={() => setShowPromoModal(false)} className="text-gray-400 hover:text-gray-600" data-testid="promo-close">
                <i className="ph-bold ph-x text-2xl"></i>
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setShowPromoModal(false);
                setPromoCode('');
              }}
            >
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="Enter code here"
                className="w-full px-4 py-3 border-2 rounded-xl mb-4 font-medium focus:outline-none focus:border-[#E54562]"
                data-testid="promo-input"
              />
              <button
                type="submit"
                className="w-full py-3 rounded-xl text-white font-bold hover:opacity-90 transition-opacity bg-[#E54562]"
                data-testid="promo-apply"
              >
                Apply Code
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default function TicketsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBookings = async () => {
      try {
        const response = await axiosInstance.get('/bookings');
        setBookings(response.data);
      } catch (error) {
        toast.error('Unable to load tickets');
      } finally {
        setLoading(false);
      }
    };

    loadBookings();
  }, []);

  const now = new Date();
  const upcoming = bookings.filter((booking) => new Date(booking.event_date) >= now);
  const past = bookings.filter((booking) => new Date(booking.event_date) < now);

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-sand flex items-center justify-center pb-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E54562]"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen pb-28 text-[#2B3A67]">
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100 pt-safe" data-testid="tickets-header">
        <div className="px-6 py-5 flex items-center justify-between">
          <h1 className="text-2xl font-extrabold tracking-tight">
            My <span className="text-[#E54562]">Tickets</span>
          </h1>
          <button className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-[#2B3A67]" data-testid="tickets-calendar-button">
            <i className="ph-bold ph-calendar-plus text-xl"></i>
          </button>
        </div>

        <div className="flex px-6 gap-8 border-b border-gray-100">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-bold ${
                activeTab === tab.id ? 'text-[#E54562] border-b-[3px] border-[#E54562]' : 'text-gray-400'
              }`}
              data-testid={`tickets-tab-${tab.id}`}
            >
              {tab.label}
              {tab.id === 'upcoming' && upcoming.length > 0 ? ` (${upcoming.length})` : ''}
            </button>
          ))}
        </div>
      </header>

      <main className="p-6 space-y-8">
        {activeTab === 'upcoming' && (
          <>
            {upcoming.length === 0 ? (
              <EmptyTickets onBrowse={() => navigate('/events')} />
            ) : (
              <>
                <TicketCard booking={upcoming[0]} />
                {upcoming.length > 1 && (
                  <div className="space-y-4" data-testid="tickets-other-upcoming">
                    <h3 className="font-bold text-lg">Other Upcoming</h3>
                    <CompactTicketCard booking={upcoming[1]} />
                  </div>
                )}
                <SafetyReminder />
              </>
            )}
          </>
        )}

        {activeTab === 'past' && (
          <>
            {past.length === 0 ? (
              <div className="text-center py-12" data-testid="tickets-past-empty">
                <i className="ph-bold ph-clock-countdown text-6xl text-gray-300 mb-4"></i>
                <p className="text-gray-400 font-medium">No past tickets</p>
              </div>
            ) : (
              past.map((booking) => <CompactTicketCard key={booking.id} booking={booking} />)
            )}
          </>
        )}

        {activeTab === 'cancelled' && (
          <div className="text-center py-12" data-testid="tickets-cancelled-empty">
            <i className="ph-bold ph-x-circle text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-400 font-medium">No cancelled tickets</p>
          </div>
        )}
      </main>
    </div>
  );
}